import { supabase } from './supabase';

export const newsService = {
  async createNews(title, content, authorName, scheduledDate = null, blocks = null, headerImage = null, userId = null, saveAsDraft = false) {
    try {
      const contentData = blocks ? JSON.stringify(blocks) : content;
      
      let status = 'draft';
      if (!saveAsDraft) {
        status = 'pending_review';
      }
      
      const { data, error } = await supabase
        .from('news')
        .insert([{
          title,
          content: contentData,
          author_name: authorName,
          scheduled_date: scheduledDate,
          header_image: headerImage,
          status: status,
          user_id: userId,
          created_by_name: authorName,
          last_edited_by_name: authorName
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async updateNews(newsId, updates, userId = null, authorName = null) {
    try {
      const processedUpdates = { ...updates };
      
      if (processedUpdates.blocks) {
        processedUpdates.content = JSON.stringify(processedUpdates.blocks);
        delete processedUpdates.blocks;
      }
      
      if (userId) {
        processedUpdates.last_edited_by_id = userId;
      }
      if (authorName) {
        processedUpdates.last_edited_by_name = authorName;
      }
      
      const { data, error } = await supabase
        .from('news')
        .update({ ...processedUpdates, updated_at: new Date().toISOString() })
        .eq('id', newsId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async publishNews(newsId, publisherId = null, publisherName = null) {
    try {
      const updateData = {
        is_published: true,
        status: 'published',
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (publisherId) {
        updateData.published_by_id = publisherId;
      }
      if (publisherName) {
        updateData.published_by_name = publisherName;
      }

      const { data, error } = await supabase
        .from('news')
        .update(updateData)
        .eq('id', newsId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  extractPlainText(content) {
    try {
      const parsed = JSON.parse(content);
      if (Array.isArray(parsed)) {
        const textBlocks = parsed.filter(b => b.type === 'text');
        return textBlocks.map(b => b.content).join(' ');
      }
    } catch (e) {
      const text = content.replace(/<[^>]*>/g, '');
      return text;
    }
    return '';
  },

  async unpublishNews(newsId) {
    try {
      const { data, error } = await supabase
        .from('news')
        .update({
          is_published: false,
          status: 'draft',
          updated_at: new Date().toISOString()
        })
        .eq('id', newsId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async deleteNews(newsId) {
    try {
      const { error } = await supabase
        .from('news')
        .delete()
        .eq('id', newsId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getPublishedNews(limit = null) {
    try {
      let query = supabase
        .from('news')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (limit) query = query.limit(limit);

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getAllNews(includeUnpublished = false) {
    try {
      let query = supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });

      if (!includeUnpublished) {
        query = query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getNewsById(newsId) {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('id', newsId)
        .single();

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async searchNews(query) {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getScheduledNews() {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('status', 'scheduled')
        .lte('scheduled_date', now)
        .order('scheduled_date', { ascending: true });

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getUserNews(userId, includeUnpublished = false) {
    try {
      let query = supabase
        .from('news')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!includeUnpublished) {
        query = query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getDraftNews() {
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('status', 'draft')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, news: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  parseContent(newsItem) {
    try {
      const parsed = JSON.parse(newsItem.content);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      if (newsItem.content && typeof newsItem.content === 'string') {
        return [{
          type: 'text',
          content: newsItem.content
        }];
      }
      return [];
    }
  }
};
